import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Radio, Vote, ShoppingBag, ExternalLink, Users, Flame } from "lucide-react";
import { toast } from "sonner";

/*  /live/{code} — the chat landing page.
    Where a streamer's fans arrive from Twitch/TikTok/YouTube/Discord/etc.
    They vote on the killer, grab a pack with 20% off, or follow back to
    the streamer. Every link preserves ?ref={code} so the streamer earns 15%.
*/

const Live = () => {
  const { code: rawCode } = useParams();
  const code = (rawCode || "").toUpperCase();
  const [data, setData]     = useState(null);
  const [voted, setVoted]   = useState(() => {
    try { return localStorage.getItem(`wm_live_voted_${code}`) || ""; } catch { return ""; }
  });
  const [voting, setVoting] = useState("");

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/streamer/live/${code}/status`);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData({ live: false, streamer: { code } });
      }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [code]);

  const vote = async (suspect_key) => {
    if (voting) return;
    setVoting(suspect_key);
    try {
      await api.post(`/live/vote/${code}`, { suspect_key });
      setVoted(suspect_key);
      try { localStorage.setItem(`wm_live_voted_${code}`, suspect_key); } catch {}
      toast.success("Vote locked in.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't register your vote.");
    } finally {
      setVoting("");
    }
  };

  if (!data) return (
    <div className="min-h-screen bg-[#0a0908] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#d4af37]" />
    </div>
  );

  // STANDBY — streamer hasn't started a case yet
  if (!data.live) return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] px-4 py-10 flex items-center justify-center">
      <div className="max-w-md text-center" data-testid="live-standby">
        <div className="w-2 h-2 rounded-full bg-[#5a5050] mx-auto mb-3" />
        <div className="text-[10px] tracking-[0.4em] text-[#5a5050] mb-3">COMMS STANDBY</div>
        <h1 className="font-display text-3xl text-[#f4f0ec] mb-2">
          @{data.streamer?.handle || code} isn't live right now.
        </h1>
        <p className="text-sm text-[#a39797] mb-6">
          Catch them when they go live — or play a case yourself.
        </p>
        <Link
          to={`/packs?ref=${code}`}
          className="btn-primary"
          data-testid="live-standby-packs"
        >
          Grab a pack (20% off) <ExternalLink size={13} />
        </Link>
      </div>
    </div>
  );

  const total = data.total_votes || 0;

  return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] relative" data-testid="live-page">
      {/* Scanline texture */}
      <div className="transmission-scanline absolute inset-0 pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* LIVE header */}
        <div className="flex items-center justify-between mb-6" data-testid="live-header">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-[#8b0000] dispatcher-pulse" />
            <div className="text-[10px] tracking-[0.4em] text-[#8b0000]">LIVE NOW</div>
          </div>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
            @{data.streamer?.handle}
          </div>
        </div>

        {/* Case title */}
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.3em] text-[#a39797] mb-1">CASE #{data.case.number}</div>
          <h1 className="font-display text-3xl sm:text-5xl text-[#f4f0ec] leading-tight">
            {data.case.title}
          </h1>
          {data.case.tagline && (
            <p className="text-sm text-[#a39797] mt-2 leading-relaxed">{data.case.tagline}</p>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 mb-6" data-testid="live-stats">
          <div className="card-noir p-3 text-center">
            <Vote size={14} className="text-[#d4af37] mx-auto mb-1" />
            <div className="font-display text-2xl text-[#f4f0ec]">{total}</div>
            <div className="text-[9px] tracking-[0.2em] text-[#5a5050]">VOTES</div>
          </div>
          <div className="card-noir p-3 text-center">
            <Users size={14} className="text-[#d4af37] mx-auto mb-1" />
            <div className="font-display text-2xl text-[#f4f0ec]">{data.viewers_count || 0}</div>
            <div className="text-[9px] tracking-[0.2em] text-[#5a5050]">WATCHING</div>
          </div>
          <div className="card-noir p-3 text-center">
            <ShoppingBag size={14} className="text-[#d4af37] mx-auto mb-1" />
            <div className="font-display text-2xl text-[#f4f0ec]">{data.buys_count || 0}</div>
            <div className="text-[9px] tracking-[0.2em] text-[#5a5050]">PACKS SOLD</div>
          </div>
        </div>

        {/* The voting board */}
        <div className="card-noir p-5 mb-6" data-testid="live-vote-board">
          <div className="flex items-center gap-2 mb-4">
            <Radio size={14} className="text-[#d4af37]" />
            <div className="text-[10px] tracking-[0.4em] text-[#d4af37]">
              {voted ? "YOUR VOTE IS IN · TAP TO CHANGE" : "WHO DID IT?"}
            </div>
          </div>
          <div className="space-y-2">
            {(data.suspects || []).map((s) => {
              const isMine = voted === s.key;
              const pct = s.percent || 0;
              return (
                <button
                  key={s.key}
                  onClick={() => vote(s.key)}
                  disabled={voting === s.key}
                  className={`w-full relative overflow-hidden text-left rounded-md border px-4 py-3 transition-colors ${
                    isMine
                      ? "border-[#d4af37] bg-[#d4af37]/5"
                      : "border-[#2a2525] bg-[#1a1515] hover:border-[#8b0000]/60"
                  }`}
                  data-testid={`live-vote-${s.key}`}
                >
                  {/* fill bar */}
                  <div
                    className="absolute inset-y-0 left-0 bg-[#8b0000]/15 transition-all duration-500 pointer-events-none"
                    style={{ width: `${pct}%` }}
                  />
                  <div className="relative flex items-center justify-between gap-3">
                    <span className={`text-sm font-medium ${isMine ? "text-[#d4af37]" : "text-[#f4f0ec]"}`}>
                      {isMine ? "✓ " : ""}{s.title}
                    </span>
                    <span className="text-xs text-[#a39797] whitespace-nowrap">
                      {s.votes} · {pct}%
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="text-[9px] text-[#5a5050] mt-3 text-center">
            One vote per device. Change your mind anytime.
          </div>
        </div>

        {/* The ask — grab a pack */}
        <div className="card-noir p-5 border-[#d4af37]/40 mb-4" data-testid="live-cta-pack">
          <div className="flex items-center gap-2 mb-2">
            <Flame size={14} className="text-[#d4af37]" />
            <div className="text-[10px] tracking-[0.4em] text-[#d4af37]">RUN THIS CASE YOURSELF</div>
          </div>
          <p className="text-sm text-[#d4d0cc] leading-relaxed mb-4">
            Love the stream? Grab your own pack with @{data.streamer?.handle}'s discount — <span className="text-[#d4af37]">20% off tonight</span>.
          </p>
          <Link
            to={`/packs?ref=${code}`}
            className="btn-primary w-full py-3"
            data-testid="live-buy-pack"
          >
            Grab a pack <ExternalLink size={13} />
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center text-[10px] text-[#5a5050] mt-8">
          wasntme.party · the internet's live mystery arena
        </div>
      </div>
    </div>
  );
};

export default Live;
